/**
 * @fileoverview Tags management page: CRUD tags and edit tag-side product membership.
 * @module features/tags/pages/TagsPage
 */
import { useEffect, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ArrowLeft, Plus } from 'lucide-react'
import { ScrollReveal } from '@/shared/components/ui'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TagFormDialog } from '../components/TagFormDialog'
import { TagRow } from '../components/TagRow'
import {
  useTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} from '../api/tags'
import type { Tag } from '../schemas/tagSchema'

type FormState =
  | { mode: 'create' }
  | { mode: 'edit'; tag: Tag }

function isConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409
}

export function TagsPage(): ReactElement {
  const { t } = useTranslation('features-tags')
  const navigate = useNavigate()
  const { data: tags, isLoading, isError } = useTagsQuery()
  const createTag = useCreateTagMutation()
  const updateTag = useUpdateTagMutation()
  const deleteTag = useDeleteTagMutation()

  const [expandedUuid, setExpandedUuid] = useState<string | null>(null)
  const [formState, setFormState] = useState<FormState | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<Tag | null>(null)

  const closeForm = (): void => {
    setFormState(null)
    setServerError(null)
  }

  const handleSubmit = async (values: { name: string }): Promise<void> => {
    if (!formState) return
    setServerError(null)
    try {
      if (formState.mode === 'create') {
        await createTag.mutateAsync(values)
      } else {
        await updateTag.mutateAsync({ uuid: formState.tag.uuid, input: values })
      }
      closeForm()
    } catch (error) {
      if (isConflict(error)) {
        setServerError(t('page.errors.duplicateName', 'A tag with that name already exists.'))
        return
      }
      throw error
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    await deleteTag.mutateAsync({ uuid: pendingDelete.uuid })
    if (expandedUuid === pendingDelete.uuid) setExpandedUuid(null)
    setPendingDelete(null)
  }

  // Esc to go back (only when no dialog is open; Esc from an editable element
  // — e.g. the product picker's search input — is left to that control)
  useEffect(() => {
    if (formState || pendingDelete) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      event.preventDefault()
      navigate(-1)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [formState, pendingDelete, navigate])

  return (
    <div className="min-h-screen pt-9">
      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 py-8 space-y-6">
        <ScrollReveal blur duration={0.3}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
                <ArrowLeft className="h-5 w-5" />
                <Kbd>Esc</Kbd>
              </Button>
              <h1 className="text-2xl font-bold text-foreground">{t('page.title', 'Tags')}</h1>
            </div>
            <Button type="button" onClick={() => setFormState({ mode: 'create' })}>
              <Plus className="size-4" />
              {t('page.newTag', 'New tag')}
            </Button>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.05} blur duration={0.3}>
          <div className="rounded-3xl border border-border/15 bg-card px-6 py-4">
            {isLoading ? (
              <div className="space-y-3 py-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : isError ? (
              <p className="py-6 text-sm text-destructive">
                {t('page.loadError', 'Failed to load tags.')}
              </p>
            ) : !tags || tags.length === 0 ? (
              <p className="py-6 text-sm text-muted-foreground">
                {t(
                  'page.empty',
                  'No tags yet. Create one to group products for collection-spend attribution.'
                )}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('page.columns.tag', 'Tag')}</TableHead>
                    <TableHead className="text-right">
                      {t('page.columns.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.map(tag => (
                    <TagRow
                      key={tag.uuid}
                      tag={tag}
                      expanded={expandedUuid === tag.uuid}
                      onToggle={() =>
                        setExpandedUuid(prev => (prev === tag.uuid ? null : tag.uuid))
                      }
                      onEdit={() => setFormState({ mode: 'edit', tag })}
                      onDelete={() => setPendingDelete(tag)}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </ScrollReveal>
      </div>

      {formState && (
        <TagFormDialog
          open
          mode={formState.mode}
          initial={
            formState.mode === 'edit' ? { name: formState.tag.name } : undefined
          }
          serverError={serverError}
          onSubmit={handleSubmit}
          onOpenChange={open => {
            if (!open) closeForm()
          }}
        />
      )}

      <Dialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('page.delete.title', 'Delete tag?')}</DialogTitle>
            <DialogDescription>
              {t(
                'page.delete.description',
                'This removes the tag and any of its product and ad-spend URL associations. Affected collection-URL spend resurfaces in the inbox. This cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={deleteTag.isPending}
            >
              {t('page.delete.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteTag.isPending}
              onClick={() => void handleConfirmDelete()}
            >
              {deleteTag.isPending
                ? t('page.delete.confirming', 'Deleting...')
                : t('page.delete.confirm', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
